import {
  BeneficiaryAggregateBooleanDimension,
  BeneficiaryAggregateEnumDimension,
  BeneficiaryAggregateMultiselectDimension,
  BeneficiaryInsightDimension,
} from '@repo/shared/dtos/beneficiary'
import { Button } from '@repo/ui/button'
import { useT } from '@repo/ui/hooks'
import { Trash2 } from 'lucide-react'
import { useSelector } from 'react-redux'

import { getCurrentOrganizationStructuredDataSchema } from '@/store/user'

import { BooleanBuckets } from './buckets/BooleanBuckets'
import { SelectModeButton as DateSelectModeButton } from './buckets/date/SelectModeButton'
import DateBuckets from './buckets/DateBuckets'
import { EnumBuckets } from './buckets/EnumBuckets'
import { MultiSelectBuckets } from './buckets/MultiSelectBuckets'
import { SelectModeButton as NumberSelectModeButton } from './buckets/number/SelectModeButton'
import { NumberBuckets } from './buckets/NumberBuckets'
import { FormType } from './types'

export default function RawDimensionForm({
  form,
  dimensionIndex,
}: {
  form: FormType
  dimensionIndex: number
}) {
  const { t } = useT({ keyPrefix: 'admin.insights.form.dimensions' })
  const dimension = form.watch(`dimensions.${dimensionIndex}`)
  const structuredDataSchema = useSelector(getCurrentOrganizationStructuredDataSchema)
  const dimensionSchema = structuredDataSchema?.find((item) => item.id === dimension.key)

  const updateDimension = (dimension: BeneficiaryInsightDimension) => {
    form.setValue(`dimensions.${dimensionIndex}`, dimension)
  }

  const removeDimension = () => {
    if (!confirm(t('removeConfirmation'))) return
    form.setValue(
      'dimensions',
      form.getValues('dimensions').filter((_, index) => index !== dimensionIndex),
    )
    const dimensionModes = form.getValues('dimensionModes')
    dimensionModes[dimensionIndex] = { date: null, number: null }
    form.setValue('dimensionModes', [...dimensionModes])
  }

  return dimensionSchema ? (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-primary-500 bg-primary-100 rounded-full px-2 py-1">
            {t(`buckets.${dimensionSchema.type}.title`)}
          </span>
          <span className="text-sm text-primary-700 font-medium">{dimensionSchema.label}</span>
        </div>
        <div className="flex items-center gap-2">
          {dimensionSchema.type === 'date' && (
            <DateSelectModeButton form={form} dimensionIndex={dimensionIndex} />
          )}
          {dimensionSchema.type === 'number' && (
            <NumberSelectModeButton form={form} dimensionIndex={dimensionIndex} />
          )}
          <Button variant="outline" size="sm" onClick={removeDimension}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      {dimensionSchema.type === 'date' && (
        <DateBuckets
          form={form}
          dimension={dimension}
          dimensionIndex={dimensionIndex}
          onChange={updateDimension}
        />
      )}
      {dimensionSchema.type === 'enum' && (
        <EnumBuckets
          form={form}
          dimension={dimension as BeneficiaryAggregateEnumDimension}
          dimensionSchema={dimensionSchema}
          onChange={updateDimension}
        />
      )}
      {dimensionSchema.type === 'number' && (
        <NumberBuckets
          form={form}
          dimension={dimension}
          dimensionIndex={dimensionIndex}
          onChange={updateDimension}
        />
      )}
      {dimensionSchema.type === 'multiselect' && (
        <MultiSelectBuckets
          dimension={dimension as BeneficiaryAggregateMultiselectDimension}
          dimensionSchema={dimensionSchema}
          onChange={updateDimension}
        />
      )}
      {dimensionSchema.type === 'boolean' && (
        <BooleanBuckets
          dimension={dimension as BeneficiaryAggregateBooleanDimension}
          onChange={updateDimension}
        />
      )}
    </div>
  ) : (
    <div>
      <Button variant="outline">Remove dimension</Button>
    </div>
  )
}
